import React, { Component } from 'react';
import { Box, Paper, Stack, Tabs, Tab, Typography, Button } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import Monitoring, { buildExportRows } from './Monitoring';
import SitesConfig from './SitesConfig';
import Agents from './Agents';
import PeriodSelector from '../PeriodSelector';
import { rowsToCSV, downloadCSV } from '../../lib/csv';

// Dashboard Modération IA — conteneur des 3 onglets.
//
// B0_02 l'a posé en coquille vide ; B2_01/02/03 ont rempli les onglets ; B3_01 lui donne son
// seul rôle métier : porter la FENÊTRE DE PÉRIODE et le bouton d'export.
//
// ⚠ Les onglets sont un ÉTAT LOCAL, pas des routes : l'URL reste #/super-admin/moderation-ia
// quel que soit l'onglet actif. Pas de routeur concurrent de DashboardLayout.
// ⚠ Class component, pas de hooks — convention du repo (cf. super-admin/CLAUDE.md).
//
// ─────────────────────────────────────────────────────────────────────────────────────────────
// POURQUOI LA PÉRIODE VIT ICI (B3_01)
//
// Les onglets sont rendus en `{tab === 'x' && <X />}` : ils sont DÉMONTÉS à chaque changement
// d'onglet. Tant que la période vivait dans Monitoring, son constructeur la recalculait à 90 j
// au retour — choisir « 7 j », passer sur « Config par site » et revenir remettait la fenêtre à
// trois mois et relançait un chargement complet. Ce n'était pas une préparation pour la Phase 2,
// c'était un bug visible. La remonter ici le corrige.
//
// DEUX COUPLES, PAS UN :
//   from / to               → ce que l'utilisateur TAPE (saisie en cours)
//   appliedFrom / appliedTo → la fenêtre RÉELLEMENT interrogée
// `PeriodSelector` distingue délibérément les deux chemins : `onChange` (saisie manuelle) stocke
// sans recharger, `onApplyPeriod` (raccourcis 7/30/90 j) stocke ET recharge. Descendre un seul
// couple et laisser Monitoring comparer `props.from`/`props.to` déclencherait un appel serveur À
// CHAQUE FRAPPE dans le champ date.
//
// `reloadToken` couvre ce que les deux couples ne peuvent pas exprimer : « Rafraîchir » sur une
// période INCHANGÉE. Sans lui, le bouton serait sans effet — aucune borne n'ayant bougé.
//
// ⛔ La barre de période et le bouton d'export ne s'affichent QUE sur l'onglet Suivi (décision
// Mathias L1, 2026-08-27) : CD2 et CD3 n'ont aucune dimension temporelle, ils y seraient inertes.
// Corollaire : la question du grisage du bouton hors de cet onglet ne se pose pas — il n'y est
// pas rendu du tout.
// ─────────────────────────────────────────────────────────────────────────────────────────────

const DAY_MS = 24 * 60 * 60 * 1000;
const DEFAULT_WINDOW_DAYS = 90; // = défaut de l'endpoint CD1 (DEFAULT_WINDOW_DAYS du contrôleur)

// YYYY-MM-DD : valeur d'un <input type="date"> ET borne envoyée à l'endpoint (cf. Funnel.js).
// ⚠ Les bornes sont TOUJOURS calculées, jamais laissées vides : le service OMET une borne vide
// de la query, l'endpoint applique alors SA fenêtre par défaut, et l'écran afficherait « du X au
// Y » sur une période différente de celle réellement calculée, sans aucune erreur.
function toInputDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const TAB_MONITORING = 'monitoring';

class ModerationDashboard extends Component {
  constructor(props) {
    super(props);
    const to = new Date();
    const from = new Date(to.getTime() - DEFAULT_WINDOW_DAYS * DAY_MS);
    const fromStr = toInputDate(from);
    const toStr = toInputDate(to);
    this.state = {
      tab: TAB_MONITORING,
      from: fromStr,
      to: toStr,
      appliedFrom: fromStr,
      appliedTo: toStr,
      reloadToken: 0,
      // Remonté par Monitoring : PeriodSelector a besoin de `isLoading`, l'export de `data`.
      monitoring: { data: null, isLoading: false, error: null },
      // Remonté par Agents (B5_01) : au moins un éditeur de consigne porte un texte modifié non
      // enregistré — le changement d'onglet le perdrait (les onglets sont démontés).
      agentsDirty: false,
    };
    this.handleTabChange = this.handleTabChange.bind(this);
    this.handleAgentsDirty = this.handleAgentsDirty.bind(this);
    this.handlePeriodChange = this.handlePeriodChange.bind(this);
    this.applyPeriod = this.applyPeriod.bind(this);
    this.onRefresh = this.onRefresh.bind(this);
    this.handleMonitoringState = this.handleMonitoringState.bind(this);
    this.exportCSV = this.exportCSV.bind(this);
  }

  handleTabChange(event, tab) {
    // B5_01 (L4, décision Mathias 2026-08-29) — confirmation BLOQUANTE : un texte de consigne
    // modifié et non enregistré serait perdu d'un clic. `beforeunload` (rechargement, fermeture)
    // est posé par PromptEditor ; la sortie par le menu latéral (Menu.js, hors write-set B5_01)
    // n'est pas couverte.
    if (this.state.agentsDirty && tab !== this.state.tab) {
      // eslint-disable-next-line no-alert
      const leave = window.confirm(
        'Une consigne modifiée n\'est pas enregistrée. Quitter l\'onglet quand même ? Le texte sera perdu.'
      );
      if (!leave) return;
    }
    this.setState({ tab, agentsDirty: false });
  }

  handleAgentsDirty(agentsDirty) {
    this.setState({ agentsDirty });
  }

  // Saisie manuelle d'une borne : on stocke, le rechargement attend « Rafraîchir ».
  handlePeriodChange(from, to) {
    this.setState({ from, to });
  }

  // Raccourci de période (7/30/90 j) : on stocke ET on applique immédiatement.
  applyPeriod(from, to) {
    this.setState((prev) => ({
      from, to, appliedFrom: from, appliedTo: to, reloadToken: prev.reloadToken + 1,
    }));
  }

  // « Rafraîchir » : on applique la saisie courante et on demande explicitement un rechargement,
  // même si les bornes n'ont pas bougé (c'est tout l'objet de reloadToken).
  onRefresh(e) {
    if (e && e.preventDefault) e.preventDefault();
    this.setState((prev) => ({
      appliedFrom: prev.from, appliedTo: prev.to, reloadToken: prev.reloadToken + 1,
    }));
  }

  handleMonitoringState(monitoring) {
    this.setState({ monitoring });
  }

  // Export CSV — la construction des lignes vit dans Monitoring.jsx (`buildExportRows`), pure et
  // testée en unitaire : c'est le SEUL oracle des trois états de fiabilité, `gc-recette-ui`
  // n'ayant aucun verbe de téléchargement.
  // ⚠ La fenêtre citée dans le fichier est la fenêtre APPLIQUÉE, pas la saisie en cours — sinon
  // l'en-tête annoncerait une période que les chiffres ne couvrent pas.
  exportCSV() {
    const { appliedFrom, appliedTo, monitoring } = this.state;
    // ⚠ Une borne peut être VIDE : l'utilisateur peut effacer un champ date. Le service l'omet
    // alors de la query et l'endpoint applique SA fenêtre — les chiffres ne couvrent donc pas la
    // borne vide. On retombe sur la fenêtre que le serveur dit avoir calculée (`data.window`),
    // sinon le fichier s'appellerait `moderation_ia__2026-08-27.csv` et son en-tête annoncerait
    // une période que son contenu ne couvre pas.
    const win = (monitoring.data && monitoring.data.window) || {};
    const from = appliedFrom || String(win.from || '').slice(0, 10);
    const to = appliedTo || String(win.to || '').slice(0, 10);
    const rows = buildExportRows(monitoring.data, { from, to });
    downloadCSV(`moderation_ia_${from}_${to}.csv`, rowsToCSV(rows));
  }

  renderPeriodBar() {
    const { from, to, monitoring } = this.state;
    const hasRows = !!(monitoring.data && (monitoring.data.by_site || []).length);
    return (
      <Box sx={{ mb: 2 }} data-testid="moderation-period-bar">
        <PeriodSelector
          from={from}
          to={to}
          isLoading={monitoring.isLoading}
          onChange={this.handlePeriodChange}
          onApplyPeriod={this.applyPeriod}
          onRefresh={this.onRefresh}
          actions={
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileDownloadOutlinedIcon />}
              disabled={monitoring.isLoading || !hasRows}
              onClick={this.exportCSV}
              data-testid="moderation-export-csv"
            >
              Exporter CSV
            </Button>
          }
        />
      </Box>
    );
  }

  render() {
    const { tab, appliedFrom, appliedTo, reloadToken } = this.state;

    return (
      <Box sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography variant="h6">Modération IA</Typography>
        </Stack>

        <Paper variant="outlined">
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tab}
              onChange={this.handleTabChange}
              variant="scrollable"
              allowScrollButtonsMobile
              aria-label="Onglets du dashboard de modération IA"
            >
              <Tab value={TAB_MONITORING} label="Monitoring" />
              <Tab value="sites" label="Config par site" />
              {/* B5_01 — ancre de recette : l'onglet n'est pas celui par défaut. */}
              <Tab value="agents" label="Prompts & agents" data-testid="agents-tab" />
            </Tabs>
          </Box>

          <Box sx={{ p: 2 }}>
            {/* ⛔ Barre et export UNIQUEMENT sur l'onglet Suivi (décision L1). */}
            {tab === TAB_MONITORING && this.renderPeriodBar()}

            {tab === TAB_MONITORING && (
              <Monitoring
                appliedFrom={appliedFrom}
                appliedTo={appliedTo}
                reloadToken={reloadToken}
                onStateChange={this.handleMonitoringState}
              />
            )}
            {tab === 'sites' && <SitesConfig />}
            {tab === 'agents' && <Agents onDirtyChange={this.handleAgentsDirty} />}
          </Box>
        </Paper>
      </Box>
    );
  }
}

export default ModerationDashboard;
